import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Button,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SearchIcon from "@mui/icons-material/Search";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 3,
  textAlign: "center",
};

const Tenders = ({ tenders, isLoading, activeTab, getTenders }) => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [tenderId, setTenderId] = useState();
  const [openNewModal, setOpenNewModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openEMDModal, setOpenEMDModal] = useState(false);
  const [tender, setTender] = useState();
  const [emdDetails, setEmdDetails] = useState();
  const [fileNames, setFileNames] = useState({
    document: "",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);

  const handleOpenNewModal = () => setOpenNewModal(true);

  const handleOpenEMDModal = async (id) => {
    setOpenEMDModal(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tender/get-by-id?tenderId=${id}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setTender(res.data);
    } catch (error) {
      console.error("Error updating tender status:", error);
    }
  };

  const handleOpenImportModal = () => setOpenImportModal(true);

  const handleClose = () => {
    setOpenNewModal(false);
    setOpenImportModal(false);
    setOpenEMDModal(false);
    setEmdDetails();
    setFileNames({ document: "" });
  };

  const handleVerifyEMD = (tenderId) => {
    localStorage.setItem("tab", "EMD");
    navigate(`/dashboard/${tenderId}`);
  };

  const handleVerifyPurchase = (tenderId) => {
    localStorage.setItem("tab", "PURCHASE");
    if (storedUser.userRole[0] === "SUPERADMIN"){
      navigate(`/superadmin-dashboard/${tenderId}`)
    } else {
      navigate(`/dashboard/${tenderId}`);
    }
  };

  const handleGoToNewForm = () => {
    localStorage.setItem("form", "NEW");
    navigate(`/vendor/dashboard/${tenderId}/qualification`);
  };

  const handleImportForm = () => {
    localStorage.setItem("form", "IMPORT");
    navigate(`/vendor/dashboard/${tenderId}/qualification`);
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params) => (
        <Link to={`/dashboard/${params.row.tenderId}`}>
          <span className="table-title">{params.row.title}</span>
        </Link>
      ),
    },
    storedUser.userRole[0] === "SUPERADMIN" &&
    {
      field: 'orgName',
      headerName: "Organization",
      flex: 1,
    },
    {
      field: "tenderType",
      headerName: "Tender Type",
      flex: 1,
      renderCell: (params) =>
        params.row.type
          ? params.row.type.charAt(0).toUpperCase() +
            params.row.type.slice(1).toLowerCase()
          : "",
    },
    {
      field: "tenderPublishDate",
      headerName: "Tender Publish Date",
      flex: 1,
    },
    {
      field: "bidSubmissionEndDate",
      headerName: "Bid Close Date",
      flex: 1,
    },
    {
      field: "vtStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        return (
          <span
            style={{
              background: "#5EC9FF",
              borderRadius: "15px",
              padding: "5px 15px",
              fontWeight: 600,
            }}
          >
            {activeTab === "TEMD"
              ? "EMD/CMD Received"
              : "Purchase Received"}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1.5,
      renderCell: (params) => (
        <>
          {activeTab === "TEMD" ? (
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() => handleVerifyEMD(params.row.tenderId)}
            >
              Verify EMD/CMD
            </Button>
          ) : (
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() => handleVerifyPurchase(params.row.tenderId)}
            >
              Verify Tender Purchase
            </Button>
          )}
        </>
      ),
    },
  ].filter(Boolean);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year} ${time}`;
  };

  const rows =
    tenders &&
    tenders.map((tender) => ({
      ...tender,
      tenderPublishDate: formatDate(tender.tenderPublishDate),
      bidSubmissionStartDate: formatDateTime(tender.bidSubmissionStartDate),
      bidSubmissionEndDate: formatDateTime(tender.bidSubmissionEndDate),
    }));

  useEffect(() => {
    const mappedRows =
      tenders &&
      tenders.map((tender) => ({
        ...tender,
        tenderPublishDate: formatDate(tender.tenderPublishDate),
        bidSubmissionStartDate: formatDateTime(tender.bidSubmissionStartDate),
        bidSubmissionEndDate: formatDateTime(tender.bidSubmissionEndDate),
      }));

    setFilteredRows(mappedRows || []);
  }, [tenders]);

  useEffect(() => {
    const lowercasedSearch = searchText.toLowerCase();

    const newFilteredRows = (rows || []).filter((row) => {
      const title = row?.tender?.title?.toLowerCase() || "";
      const tenderType = row?.tender?.type?.toLowerCase() || "";
      const status = row?.vtStatus?.toLowerCase() || "";
      const publishDate = row?.tender?.tenderPublishDate
        ? formatDate(row.tender.tenderPublishDate).toLowerCase()
        : "";

      const bidCloseDate = row?.tender?.bidSubmissionEndDate
        ? formatDateTime(row.tender.bidSubmissionEndDate).toLowerCase()
        : "";

      return (
        title.includes(lowercasedSearch) ||
        tenderType.includes(lowercasedSearch) ||
        status.includes(lowercasedSearch) ||
        publishDate.includes(lowercasedSearch) ||
        bidCloseDate.includes(lowercasedSearch)
      );
    });

    setFilteredRows(newFilteredRows);
  }, [searchText]);

  const handleEMDChange = (e) => {
    setEmdDetails({ ...emdDetails, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;

    if (files && files.length > 0) {
      const file = files[0]; // Get the first file

      // Update the file names state
      setFileNames((prev) => ({
        ...prev,
        [name]: file.name, // Set the filename in state
      }));

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/file/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          }
        );
        // Set the received file path in tender state
        setEmdDetails({ ...emdDetails, [e.target.name]: res.data });
      } catch (error) {
        console.error("File upload error:", error);
      }
    }
  };

  const handleEMDSubmit = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-emd`,
        {
          vendorId: vendorId,
          tenderId: tender.tenderId,
          utr: emdDetails.utr,
          document: emdDetails.document,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setOpenEMDModal(false);
      setEmdDetails();
    } catch (error) {
      console.error("Error updating tender status:", error);
    }
  };
  return (
    <Box sx={{ background: "#fff" }}>
      <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
        <TextField
          placeholder="Search"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{
            borderRadius: "12px",
            background: "#fff",
            width: 300,
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            "&:hover": { background: "#D8E9F0" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        disableColumnResize
        getRowId={(row) => row.tenderId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        sx={{
          position: "relative",
          border: "none",
          fontFamily: "Inter !important",

          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700 !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "none !important",
            color: "black",
            fontSize: "14px",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-cell": {
            fontSize: "13px",
            border: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
            color: "#000 !important",
            border: "1px solid #B0B0B0",
            borderRadius: "12px",
            padding: 1,
          },
          "& .MuiInputBase-root-MuiInput-root::after": {
            borderBottom: "none !important",
          },
          "& .MuiInputBase-root-MuiInput-root": {
            borderBottom: "none !important",
          },
          [`& .${gridClasses.row}.even`]: {
            backgroundColor: "#EAF4FF",
            // '&:hover': {
            //   backgroundColor: "#f6f6f6",
            // },
          },
          "& .MuiInput-underline:before": {
            borderBottom: "none !important",
          },
          "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
            borderBottom: "none !important",
          },
        }}
      />

      {/* <Modal
        open={openNewModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-description">
            Please fill your first form carefully, you can reuse details from
            this in your future forms.
          </Typography>
          <Button className="btn-contained" onClick={handleGoToNewForm}>
            Acknowledge
          </Button>
        </Box>
      </Modal>

      <Modal
        open={openImportModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-description">
            "We have details from your last submitted vendor qualification. Do
            you want to fetch data from there?
            <br />
            <br />
            You can still edit any information that you want."
          </Typography>
          <Box className="btn-container" sx={{ gap: 4, width: "80%" }}>
            <Button
              className="btn-outlined"
              sx={{ width: "100%" }}
              onClick={handleGoToNewForm}
            >
              Blank Form
            </Button>
            <Button
              className="btn-contained"
              sx={{ width: "100%" }}
              onClick={handleImportForm}
            >
              Import
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openEMDModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style, textAlign: "start" }}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            textAlign={"start"}
          >
            Enter EMD Details
          </Typography>
          <Typography id="modal-modal-description" mb={2}>
            Please enter EMD details below <br />
            <br />
            Please transfer the EMD amount of{" "}
            <strong>Rs.{tender?.emdAmount} on the below account</strong>
            <br />
            Account Holder Name: <strong>{tender?.accountName}</strong>
            <br />
            Account Number: <strong>{tender?.accountNumber}</strong>
            <br />
            Bank Name: <strong>{tender?.emdBank}</strong>
            <br />
            IFSC Code: <strong>{tender?.ifsc}</strong>
            <br />
            <br />
            Once paid, please enter the details below
          </Typography>
          <label htmlFor="utr">UTR Number</label>
          <input
            type="text"
            name="utr"
            id="utr"
            placeholder="Enter Your UTR Number Here"
            onChange={handleEMDChange}
          />
          <label htmlFor="document">
            Upload Document
            <div className="input-file">
              <input
                type="file"
                id="document"
                name="document"
                onChange={handleFileUpload}
              />
              <span
                style={{
                  width: "80%",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {fileNames.document || "No file chosen"}
              </span>
              <AttachFileIcon
                fontSize="small"
                sx={{
                  color: "#555",
                  transform: "rotate(90deg)",
                  "&:hover": { color: "#1A71F6BF" },
                }}
              />
            </div>
          </label>
          <Box className="btn-container" sx={{ gap: 4, width: "80%" }}>
            <Button className="btn-contained" onClick={handleEMDSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      </Modal> */}
    </Box>
  );
};

export default Tenders;
